import Container from 'widget/components/Progress/primitives/Container';
import formatDuration from 'widget/components/Progress/formatDuration';
import player from 'widget/player';
import Slider from 'widget/components/Progress/primitives/Slider';
import Time from 'widget/components/Progress/primitives/Time';
import useMount from 'hooks/useMount';
import Waveform from 'widget/components/Progress/Waveform';
import { useCallback, useEffect, useState } from 'react';
import { usePlayerProgress } from 'widget/hooks';
import type { ChangeEvent } from 'react';

type Props = {
  notYetInteractedWith?: boolean;
  scrubDisabled?: boolean;
  supportMobileView: boolean;
  hide?: boolean;
};

function Progress({
  notYetInteractedWith,
  scrubDisabled,
  supportMobileView,
  hide = false,
}: Props) {
  const [hasPlayed, setHasPlayed] = useState(false);
  const [disabled, setDisabled] = useState(true);
  const [dragging, setIsDragging] = useState(false);
  const [progress, setProgress] = usePlayerProgress(dragging);

  useEffect(() => {
    if (hasPlayed) setDisabled(!!scrubDisabled);
  }, [hasPlayed, scrubDisabled]);

  useMount(() =>
    player.subscribe({
      play: () => setHasPlayed(true),
    }),
  );

  const seek = useCallback<(event: ChangeEvent<HTMLInputElement>) => void>(
    e => setProgress(Number(e.currentTarget.value)),
    [setProgress],
  );

  const onMouseDown = useCallback<() => void>(
    () => setIsDragging(true),
    [setIsDragging],
  );

  const onMouseUp = useCallback<() => Promise<void>>(async () => {
    setIsDragging(false);
    await player.seek(progress.position);
  }, [progress.position, setIsDragging]);

  return (
    <Container
      data-test="progress-container"
      hide={hide}
      notYetInteractedWith={notYetInteractedWith}
      supportMobileView={supportMobileView}
    >
      <Time data-test="current-time" show={progress.position > 0}>
        {formatDuration(progress.position)}
      </Time>
      <Time show={progress.position > 0}>
        {formatDuration(progress.duration)}
      </Time>
      <Slider
        aria-label="Progress Slider"
        data-test="progress-slider"
        disabled={disabled}
        max={progress.duration}
        min={0}
        onChange={seek}
        onMouseDown={onMouseDown}
        onMouseUp={onMouseUp}
        onTouchEnd={onMouseUp}
        onTouchStart={onMouseDown}
        step={1}
        type="range"
        value={progress.position}
      />
      <Waveform duration={progress.duration} position={progress.position} />
    </Container>
  );
}

export default Progress;
